import catalog from './leave-rule-catalog.json';

/**
 * The leave rule catalog (leave-rule-catalog.json), typed. The same file is built into the leave
 * workflows (blocks/workflows/leave/*.json), which enforce it; the app reads it to decide what to
 * offer. A rule applies to an employee who holds its permission; in a group, only the held rule
 * with the highest priority applies. Nothing here depends on a country or region.
 */

export interface LeaveRuleParams {
  noticePeriod: { minDaysNotice: number };
  maxConsecutiveDays: { maxDays: number };
  sickLeaveCertificate: { minDays: number };
  sickLeaveDoctorSends: Record<string, never>;
  crossHrPeriod: Record<string, never>;
  toilBalance: { splitByCategory: boolean };
  missedAttendance: { exemptFlexible: boolean };
}

export type LeaveRuleGroup = keyof LeaveRuleParams;

export interface LeaveRule<G extends LeaveRuleGroup = LeaveRuleGroup> {
  code: string;
  group: G;
  permission: string;
  priority: number;
  description: string;
  params: LeaveRuleParams[G];
}

export type ActiveLeaveRules = { [G in LeaveRuleGroup]?: LeaveRule<G> };

export const LEAVE_RULES = catalog.rules as LeaveRule[];

/** Titles of the categories suggested as an HR term's default leave, in order of preference. */
export const DEFAULT_LEAVE_CATEGORIES: string[] = catalog.defaultLeaveCategories;

const DEFAULT_LEAVE_CATEGORIES_BY_EMPLOYMENT_TYPE: Record<string, string[]> =
  catalog.defaultLeaveCategoriesByEmploymentType;

/** Legacy records store compact employment types ("FullTime"); compare without spaces and case. */
const compact = (value: string) => value.replace(/[\s-]/g, '').toLowerCase();

/** The default leave titles suggested for an employment type, in order of preference (an intern or
 * probationer gets casual leave first, everyone else annual). */
export const defaultLeaveCategoriesFor = (employmentType?: string): string[] => {
  const type = compact(employmentType ?? '');
  const match = Object.entries(DEFAULT_LEAVE_CATEGORIES_BY_EMPLOYMENT_TYPE).find(
    ([key]) => compact(key) === type
  );
  return match?.[1] ?? DEFAULT_LEAVE_CATEGORIES;
};

/** One leave category of an HR term, as far as picking the default leave goes. */
interface DefaultLeaveCandidate {
  LeaveCategory: string;
  IsApplicable?: boolean;
  IsUnlimited?: boolean;
  IsDefault?: boolean;
}

/** A category that can be the term's default leave: applicable and with a day limit to count against. */
export const canBeDefaultLeave = (allotment: DefaultLeaveCandidate): boolean =>
  Boolean(allotment.IsApplicable) && !allotment.IsUnlimited;

const sameTitle = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/** The default leave suggested for a term: the first category that can be one in the employment
 * type's order of titles, else the first category that can be one. */
export const suggestDefaultLeave = <T extends DefaultLeaveCandidate>(
  allotments: T[],
  employmentType?: string
): T | undefined => {
  const candidates = allotments.filter(canBeDefaultLeave);
  return (
    defaultLeaveCategoriesFor(employmentType)
      .map((title) => candidates.find((allotment) => sameTitle(allotment.LeaveCategory, title)))
      .find((allotment): allotment is T => Boolean(allotment)) ?? candidates[0]
  );
};

/** The HR term's default leave, where TOIL and missed attendance count: the category it marks
 * `IsDefault`, or for a term saved before that field existed, the suggested one. */
export const defaultLeaveOf = <T extends DefaultLeaveCandidate>(
  allotments: T[],
  employmentType?: string
): T | undefined =>
  allotments.find((allotment) => allotment.IsDefault && canBeDefaultLeave(allotment)) ??
  suggestDefaultLeave(allotments, employmentType);

/** The rule of each group that binds the holder of `held`: in a group, the highest priority held. */
export const activeLeaveRules = (held: (permission: string) => boolean): ActiveLeaveRules => {
  const byGroup: Partial<Record<LeaveRuleGroup, LeaveRule>> = {};
  for (const rule of LEAVE_RULES) {
    if (!held(rule.permission)) continue;
    const current = byGroup[rule.group];
    if (!current || rule.priority > current.priority) byGroup[rule.group] = rule;
  }
  return byGroup as ActiveLeaveRules;
};
